import debounce from 'lodash.debounce';
import { useEffect, useState } from 'react';
const updatePopoverPosition = ({ ref, triggerRef, alignment, withTip, placement }) => {
    const popover = ref.current;
    const trigger = triggerRef.current;
    if (!popover || !trigger)
        return;
    const root = document.documentElement;
    const pM = parseInt(window.getComputedStyle(root).getPropertyValue('--page-margin'));
    popover.style.left = '';
    popover.style.width = '';
    popover.style.right = '';
    const triggerRect = trigger.getBoundingClientRect();
    const popoverRect = popover.getBoundingClientRect();
    const windowInnerWidth = window.innerWidth;
    const windowInnerHeight = window.innerHeight;
    const triggerLeft = Math.round(triggerRect.left);
    const triggerRight = Math.round(triggerRect.right);
    const triggerWidth = Math.round(triggerRect.width);
    const popoverWidth = Math.round(popoverRect.width);
    const tipWidth = 23;
    const tipHeight = 11.5;
    let leftPosition = triggerLeft;
    let rightPosition = windowInnerWidth - triggerRight;
    const tipTriggerAlignValue = triggerWidth / 2 - tipWidth / 2;
    const getTipCentered = (tipLeft) => tipLeft + tipTriggerAlignValue;
    const popoverOffset = windowInnerWidth - popoverWidth - pM;
    let popoverTop;
    let actualPlacement;
    const tipOffset = 20;
    const tipTopPlacement = popoverRect.height;
    const tipBottomPlacement = -tipHeight;
    const hasRoomAbove = triggerRect.top - tipOffset - popoverRect.height >= 0;
    const hasRoomBelow = triggerRect.bottom + tipOffset + popoverRect.height <= windowInnerHeight;
    const placeAbove = (placement === 'top' && hasRoomAbove) ||
        (placement === 'bottom' && !hasRoomBelow);
    if (placeAbove) {
        popoverTop = triggerRect.top - tipOffset - popoverRect.height;
        popover.style.setProperty('--tip-top', `${tipTopPlacement}px`);
        actualPlacement = 'top';
    }
    else {
        popoverTop = triggerRect.bottom + tipOffset;
        popover.style.setProperty('--tip-top', `${tipBottomPlacement}px`);
        actualPlacement = 'bottom';
    }
    popover.style.top = `${popoverTop}px`;
    if (windowInnerWidth < 768) {
        popover.style.left = `${pM}px`;
        popover.style.right = `${pM}px`;
        const tipLeft = triggerLeft - pM;
        popover.style.setProperty('--tip-left', `${getTipCentered(tipLeft)}px`);
        return actualPlacement;
    }
    switch (alignment) {
        case 'left': {
            leftPosition = triggerLeft;
            if (leftPosition + popoverWidth > windowInnerWidth - pM) {
                leftPosition = popoverOffset;
            }
            if (leftPosition < pM) {
                leftPosition = pM;
            }
            popover.style.left = `${leftPosition}px`;
            if (withTip) {
                const tipLeft = triggerLeft - leftPosition + tipTriggerAlignValue;
                popover.style.setProperty('--tip-left', `${tipLeft}px`);
            }
            break;
        }
        case 'right': {
            rightPosition = windowInnerWidth - triggerRight;
            if (rightPosition + popoverWidth > windowInnerWidth - pM) {
                rightPosition = popoverOffset;
            }
            if (rightPosition < pM) {
                rightPosition = pM;
            }
            popover.style.right = `${rightPosition}px`;
            if (withTip) {
                const tipRight = windowInnerWidth -
                    (rightPosition + triggerRight) +
                    tipTriggerAlignValue;
                popover.style.setProperty('--tip-left', 'unset');
                popover.style.setProperty('--tip-right', `${tipRight}px`);
            }
            break;
        }
        case 'center': {
            let leftPosForCenteredPopover = triggerLeft + triggerWidth / 2 - popoverWidth / 2;
            if (leftPosForCenteredPopover + popoverWidth > windowInnerWidth - pM) {
                leftPosForCenteredPopover = popoverOffset;
            }
            if (leftPosForCenteredPopover < pM) {
                leftPosForCenteredPopover = pM;
            }
            popover.style.left = `${leftPosForCenteredPopover}px`;
            if (withTip) {
                const tipLeft = triggerLeft - leftPosForCenteredPopover;
                popover.style.setProperty('--tip-left', `${getTipCentered(tipLeft)}px`);
            }
            break;
        }
    }
    return actualPlacement;
};
export const usePopoverPosition = ({ ref, triggerRef, alignment, placement, open, withTip }) => {
    const [actualPlacement, setActualPlacement] = useState(null);
    useEffect(() => {
        const debouncedUpdatePopoverPosition = debounce(() => {
            const newPlacement = updatePopoverPosition({
                ref,
                triggerRef,
                alignment,
                placement,
                withTip
            });
            setActualPlacement(newPlacement ?? null);
        }, 80);
        document.fonts.onloadingdone = () => {
            const newPlacement = updatePopoverPosition({
                ref,
                triggerRef,
                alignment,
                placement,
                withTip
            });
            setActualPlacement(newPlacement ?? null);
        };
        const initialPlacement = updatePopoverPosition({
            triggerRef,
            alignment,
            placement,
            ref,
            withTip
        });
        setActualPlacement(initialPlacement ?? null);
        const handleResize = () => {
            debouncedUpdatePopoverPosition();
        };
        window.addEventListener('resize', handleResize, { passive: true });
        return () => {
            window.removeEventListener('resize', handleResize);
            debouncedUpdatePopoverPosition.cancel();
        };
    }, [ref, alignment, placement, open, withTip]);
    return actualPlacement;
};
//# sourceMappingURL=use-popover-position.js.map